/**
 * TenantBrandingContext — fetches /api/tenants/me/branding once after
 * login and applies the result to the document root as CSS custom
 * properties + exposes the resolved blob via `useBranding()`.
 *
 * Foundation for Gemini Space "Rogue" multi-tenant shell — every brand
 * decision (colors, logo, app name, AI name, "Powered by" footer) flows
 * from a single tenant record so onboarding a second tenant is just a
 * Mongo document.
 *
 * Pre-login default: **Rogue / Rogi** — the public Geminii brand.
 * Hidroverse (and any white-labelled tenant) overrides this after
 * /tenants/me/branding hydrates post-login.
 *
 * CSS custom properties set on <html>:
 *   --brand-primary
 *   --brand-accent
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const DEFAULTS = {
  primary_color: "#1FB6C1",
  accent_color: "#A78BFA",
  logo_url: null,
  app_name: "Rogue",
  ai_name: "Rogi",
  footer_text: null,
  powered_by_visible: true,
  support_email: null,
  support_phone: null,
};

// 2026-02-20 (iter 172, Dirk on production) — cache the host-resolved
// branding in localStorage keyed by hostname so the LOGIN page hydrates
// the right brand SYNCHRONOUSLY on next open. Before this, a cold PWA
// launch flashed the public Rogue logo for ~600ms while the
// `by-domain` probe ran, which on hidroverse.co.za looked like
// "Gemini Rogue leaking into the HV PWA". The cached entry is keyed by
// host so each tenant's domain has its own pre-paint state.
const CACHE_KEY = "hv_branding_by_host";

function _readCachedForHost() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw);
    const host = (window.location.hostname || "").toLowerCase();
    return (all && typeof all === "object" && all[host]) || null;
  } catch {
    return null;
  }
}

function _writeCachedForHost(payload) {
  if (typeof window === "undefined") return;
  try {
    const host = (window.location.hostname || "").toLowerCase();
    if (!host) return;
    const raw = localStorage.getItem(CACHE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[host] = { ...payload, _cached_at: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(all));
  } catch {
    /* localStorage may be full / disabled; ignore */
  }
}

const BrandingCtx = createContext({
  branding: DEFAULTS,
  modules: {},
  tenantId: null,
  loading: false,
  refresh: async () => {},
});

export function TenantBrandingProvider({ children }) {
  const { user } = useAuth();
  // Synchronously hydrate from per-host cache so the login flash is
  // gone on the SECOND+ visit (and the first ever visit if the user
  // is reopening a PWA they previously had branding for).
  const initialCached = _readCachedForHost();
  const initialBranding = initialCached?.branding
    ? { ...DEFAULTS, ...initialCached.branding }
    : DEFAULTS;
  const [branding, setBranding] = useState(initialBranding);
  const [modules, setModules] = useState({});
  const [tenantId, setTenantId] = useState(initialCached?.tenant_id || null);
  const [plan, setPlan] = useState(null);
  const [planLabel, setPlanLabel] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);

  // [Iter 277] Host-aware brand override when the by-domain probe is
  // failing (e.g. production SSL handshake on a freshly-attached
  // custom domain). If the host pattern says HidroVerse AND the brand
  // still equals the Rogue default, force the HV fallback so the
  // document title and PWA installer don't show "Rogue · Rogi" on a
  // HidroVerse host. Same logic as Login.jsx pickFallbackAppName().
  const _isHidroVerseHost = () => {
    if (typeof window === "undefined") return false;
    const h = (window.location.host || "").toLowerCase();
    return h.includes("hidroverse") || h.startsWith("hidroverse-rogue.");
  };

  const apply = (b) => {
    if (typeof document === "undefined") return;
    const r = document.documentElement;
    if (b.primary_color) r.style.setProperty("--brand-primary", b.primary_color);
    if (b.accent_color)  r.style.setProperty("--brand-accent",  b.accent_color);
    // Drive document title from the brand so the browser tab + PWA
    // launcher show the right product name on day one.
    // Iter 277 — when the brand is still the public Rogue default and
    // the host pattern says HidroVerse, override to SalesFlow · Hidro
    // so a failing probe never leaks "Rogue · Rogi" on a HV host.
    let _appName = b.app_name;
    let _aiName = b.ai_name;
    if ((_appName || "").toLowerCase() === "rogue" && _isHidroVerseHost()) {
      _appName = "SalesFlow";
      _aiName = "Hidro";
    }
    if (_appName) {
      try { document.title = `${_appName}${_aiName ? ` · ${_aiName}` : ""}`; } catch { /* ignore */ }
    }
  };

  // [Iter 290zp.13m] Host-aware brand override at the React-state
  // layer. Previously only `apply()` patched the document title; the
  // `branding` state stayed at Rogue defaults, which leaked "Rogue"
  // text and the bundled Rogue logo image into the customer portal on
  // hidroverse hosts when the by-domain probe failed. We apply the
  // override at the consumer-facing memo, NOT via setBranding in an
  // effect (which would violate react-hooks/set-state-in-effect and
  // risk re-render churn). The raw `branding` state is unchanged;
  // every consumer that reads from this context receives the
  // host-corrected view.
  const HV_FALLBACK_LOGO_URL =
    "https://customer-assets.emergentagent.com/job_tech-dispatch-55/artifacts/qjyr3rrs_hidroverse%20%285%29.png";
  // [Iter 290zp.13n] Light-mode logo — same teal/grey droplet + "HidroVerse"
  // wordmark but coloured so it reads on a light surface (the dark/standard
  // logo is white-on-transparent and disappears in light mode).
  const HV_FALLBACK_LOGO_LIGHT_URL =
    "https://customer-assets.emergentagent.com/job_tech-dispatch-55/artifacts/h9ya1a43_hidroverse%20%286%29.png";
  const effectiveBranding = useMemo(() => {
    // [Iter 290zp.13u — HV logo restore]
    // On any HidroVerse host we FORCE HidroVerse branding regardless
    // of what's in the tenant DB. Previously this override only
    // fired when `branding.app_name === "rogue"`, but production HV
    // tenants have `app_name === "SalesFlow"`, so the override
    // short-circuited and the Rogue logo (stored on the tenant doc
    // by mistake during an earlier migration) leaked through.
    // Scope: branding only. No data scoping changes — that is
    // tracked separately and escalated to Emergent platform support.
    if (_isHidroVerseHost()) {
      return {
        ...branding,
        app_name: "SalesFlow",
        ai_name: "Hidro",
        logo_url: HV_FALLBACK_LOGO_URL,
        logo_url_light: HV_FALLBACK_LOGO_LIGHT_URL,
        logo_url_dark: HV_FALLBACK_LOGO_URL,
      };
    }
    return branding;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants/me/branding");
      const merged = { ...DEFAULTS, ...(data?.branding || {}) };
      setBranding(merged);
      setModules(data?.modules || {});
      setTenantId(data?.tenant_id || null);
      setPlan(data?.plan || null);
      setPlanLabel(data?.plan_label || null);
      setFeatures(Array.isArray(data?.features) ? data.features : []);
      apply(merged);
      // Persist branding by host so future cold-opens render the
      // right brand on the LOGIN page (the post-login refresh is the
      // source of truth — it has the freshest copy).
      _writeCachedForHost({ branding: merged, tenant_id: data?.tenant_id || null });
    } catch {
      // Defaults already applied — silent failure is the right call
      apply(DEFAULTS);
    } finally {
      setLoading(false);
    }
  };

  // [2026-02-19 — Stage 2] Pre-login: resolve branding by current
  // hostname so the Login page shows the right tenant's logo /
  // colours / app name BEFORE the user signs in. Falls back to the
  // public Rogue/Rogi brand if the host doesn't map to a tenant.
  const refreshByHost = async () => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname;
    try {
      const { data } = await api.get("/_geminii/tenants/by-domain", { params: { host } });
      const merged = { ...DEFAULTS, ...(data?.branding || {}) };
      setBranding(merged);
      setTenantId(data?.id || null);
      apply(merged);
      // Persist for next cold-open so the login flash is gone.
      _writeCachedForHost({ branding: merged, tenant_id: data?.id || null });
    } catch {
      // No tenant for this host — keep Rogue/Rogi defaults.
      apply(DEFAULTS);
    }
  };

  // Re-fetch whenever the user changes (login / logout / re-login).
  // Pre-login: resolve by host so the Login page is white-labelled.
  // Deferred via queueMicrotask so the state-setting happens outside
  // the effect's synchronous frame — this is the documented escape
  // from react-hooks/set-state-in-effect for legitimate fire-and-forget
  // network calls that update state on completion.
  useEffect(() => {
    if (typeof queueMicrotask === "function") {
      queueMicrotask(() => { if (!user) { refreshByHost(); } else { refresh(); } });
    } else {
      Promise.resolve().then(() => { if (!user) { refreshByHost(); } else { refresh(); } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value = useMemo(
    () => ({ branding: effectiveBranding, modules, tenantId, plan, planLabel, features, loading, refresh }),
    [effectiveBranding, modules, tenantId, plan, planLabel, features, loading],
  );

  return <BrandingCtx.Provider value={value}>{children}</BrandingCtx.Provider>;
}

export function useBranding() {
  return useContext(BrandingCtx);
}

/**
 * useFeature("white_label") — boolean. True if the current tenant's
 * plan (plus any per-tenant overrides) grants the named feature.
 *
 * Use for inline UI gating. For a paywall overlay use
 * <FeatureGate feature="..."> from this same module.
 *
 * NOTE: Always defaults to FALSE while branding context is still
 * loading — the UI may briefly show locked state during initial
 * hydration, then unlock. That's correct: better to fail closed.
 */
export function useFeature(name) {
  const { features } = useContext(BrandingCtx);
  if (!name) return true;
  return Array.isArray(features) && features.includes(name);
}

/**
 * <FeatureGate feature="white_label" fallback={...}>
 *   <BrandingCard />
 * </FeatureGate>
 *
 * Wraps a UI region in a feature check. By default, hides the children
 * entirely when the feature isn't granted. Pass `fallback` to render
 * an upsell card instead. Pass `mode="lock"` to render the children
 * but visually grey them out with a 🔒 overlay.
 */
export function FeatureGate({ feature, children, fallback = null, mode = "hide" }) {
  const has = useFeature(feature);
  if (has) return children;
  if (mode === "lock") {
    return (
      <div className="relative" data-testid={`feature-locked-${feature}`}>
        <div className="opacity-40 pointer-events-none select-none">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px] rounded">
          <UpsellBadge feature={feature} />
        </div>
      </div>
    );
  }
  return fallback;
}

/** UpsellBadge — small "🔒 Requires Growth plan" chip used by FeatureGate
 *  in lock-mode and by inline upsell prompts. */
export function UpsellBadge({ feature }) {
  const { planLabel } = useContext(BrandingCtx);
  // Best-effort upsell label without an extra round-trip — the precise
  // upgrade path is computed server-side when a 402 is returned.
  return (
    <div
      className="text-xs uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200"
      data-testid={`upsell-badge-${feature}`}
      title={`Your ${planLabel || "current"} plan doesn't include this feature.`}
    >
      🔒 Upgrade required
    </div>
  );
}

/** Convenience: is a per-tenant module enabled? Defaults to true when the
 * module name isn't in the tenant's flag set (so newly-shipped features
 * are on by default for existing tenants). */
export function useModuleEnabled(name) {
  const { modules } = useContext(BrandingCtx);
  if (!name) return true;
  return modules?.[name] !== false;
}
